// shared-lib/millwork/room.js
/**
 * A room's millwork: where its baseboard runs, its casing and its crown. The
 * take-off, the 3D view and the panels all call this, so they cannot
 * disagree (spec §2). Pure.
 */
// the take-off's own hydration (estimate-pricing re-exports this copy)
const { hydrateRoomOpenings } = require('../openings/roomOpeningHydration');
const { getRoomFloorY, assignWallBlockers } = require('./blockers');
const { openingsOnWall } = require('./wallFrame');
const { baseboardForWall } = require('./baseboard');
const { casingForRoom } = require('./casing');
const { crownForWall } = require('./crown');

const NO_TYPE = () => null;

// One wall's run, given the blockers `assignWallBlockers` put on it.
const runForWall = (room, wall, openings, blockers, floorY, materialTypeOf) =>
  baseboardForWall({
    wall,
    openings: openingsOnWall(wall, openings),
    blockers,
    objects: room?.objects,
    floorY,
    materialTypeOf,
  });

// The room's walls with `wall` standing in for the room wall it copies (same
// id), or added when the room has no such wall. `assignWallBlockers` keys its
// answer by wall OBJECT and decides an appliance's wall among all of them, so
// a copy has to be one of the walls it is handed.
const wallsWith = (room, wall) => {
  const walls = room?.walls || [];
  const at = walls.findIndex(
    (w) => w === wall || (wall?.id != null && w?.id === wall.id)
  );
  if (at < 0) return [...walls, wall];
  return walls.map((w, i) => (i === at ? wall : w));
};

/**
 * One wall's baseboard run — what `millworkForRoom` gives that wall. For a
 * caller drawing one wall at a time (the 3D view), whose `wall` may be a copy
 * of the room's wall carrying a trim edit not yet saved: the copy's own
 * `trim` decides.
 *
 * @param {Object} room
 * @param {Object} wall  the room wall, or a copy of it (same id)
 * @param {Object[]} openings  the room's HYDRATED doors, windows and openings
 * @param {Object} [opts]
 * @param {(region) => string|null} [opts.materialTypeOf]
 * @returns {Object|null} null when the wall's baseboard is not switched on
 */
const baseboardForRoomWall = (
  room,
  wall,
  openings,
  { materialTypeOf = NO_TYPE } = {}
) => {
  const floorY = getRoomFloorY(room);
  const blockers = assignWallBlockers(
    wallsWith(room, wall),
    room?.objects,
    floorY
  ).get(wall);
  return runForWall(room, wall, openings, blockers, floorY, materialTypeOf);
};

/**
 * @param {Object} room
 * @param {Object} [opts]
 * @param {Object} [opts.structureOpenings] `{ doors, windows, openings }` canonical arrays
 * @param {(region) => string|null} [opts.materialTypeOf]
 * @returns {{ floorY: number, baseboard: Array<Object|null>, casing: Object[], crown: Array<Object|null> }}
 *   `baseboard[i]` is `room.walls[i]`'s run, null where the wall has none
 */
const millworkForRoom = (
  room,
  { structureOpenings = null, materialTypeOf = NO_TYPE } = {}
) => {
  const walls = room?.walls || [];
  const hydrated = structureOpenings
    ? hydrateRoomOpenings(room, structureOpenings)
    : room;
  const openings = [
    ...(hydrated?.doors || []),
    ...(hydrated?.windows || []),
    ...(hydrated?.openings || []),
  ];
  const floorY = getRoomFloorY(room);
  // once per room — `baseboardForRoomWall` per wall would redo it each time
  const blockersByWall = assignWallBlockers(walls, room?.objects, floorY);
  const baseboard = walls.map((wall) =>
    runForWall(
      room,
      wall,
      openings,
      blockersByWall.get(wall),
      floorY,
      materialTypeOf
    )
  );
  const casing = casingForRoom(room, {
    doors: hydrated?.doors,
    windows: hydrated?.windows,
    openings: hydrated?.openings,
  });
  const crown = walls.map((wall) =>
    crownForWall({
      wall,
      openings: openingsOnWall(wall, openings),
      floorY,
      room,
    })
  );
  return { floorY, baseboard, casing, crown };
};

module.exports = { millworkForRoom, baseboardForRoomWall };
